(function TripSummaryScheduleComponent($, Adzuki)
{
	'use strict';

	const component = {
		emits: [
			'addImagesToComponent',
			'collapsedChange',
			'delete',
			'deleteComponentImage',
			'edit',
			'hideUnhideComponent',
			'hideUnhideComponentVendor',
			'moveComponent',
			'setComponentMainImage',
			'updateComponentCruiseDayImage',
			'updateComponentDescription',
			'updateComponentTitle',
			'updateComponentTravelers',
		],
		inject: [
			'getFaIconStyle',
			'formatDate',
			'formatTime',
		],
		props: {
			sectionMode: {
				type   : Boolean,
				default: false,
			},
			component: {
				type   : Object,
				default: {},
			},
			fitBuilderTypeId: {
				type   : Number,
				default: 1,
			},
			itinerary: {
				type    : Object,
				required: false,
				default : {},
			},
			oneWayCounts: {
				type   : Map,
				default: new Map(),
			},
			summaryTravelers: {
				type    : Array,
				required: true,
			},
		},
		template: '#ts-schedule-component',
		data() {
			return {
				baseUri                   : Adzuki.base_uri,
				editingTitle              : false,
				titleDraft                : '',
				loadingMessage            : '',
				selectedCruiseDay         : null,
				showAttachmentModal       : false,
				showAddComponentMediaModal: false,
				showReplaceDayImageModal  : false,
				showPlayVideoModal        : false,
				playVideoHtml             : '',
				currentVideo              : null,
				// default to collapsed: fully-expanded cards mean two components fill
				// the viewport and a two-week trip is an enormous scroll. '!== 0'
				// rather than '=== 1' so anyone who has explicitly chosen the
				// expanded view still gets it.
				collapsed                 : window.localStorage.getItem('ts_compact_view') !== '0',
			};
		},
		mounted() {
			// follow the itinerary-wide compact view toggle
			this.compactViewHandler = (event) => {
				this.collapsed = event.detail.compact;
			};

			// follow the per-day collapse toggle
			this.compactDayHandler = (event) => {
				if (event.detail.dayNumber === parseInt(this.component.day_number)) {
					this.collapsed = event.detail.compact;
				}
			};

			// the edit modal closing without a save leaves this card's
			// 'Editing Component...' overlay orphaned (a save hard-reloads the
			// page, so only the cancel path needs this)
			this.componentModalClosedHandler = () => {
				if (this.loadingMessage === 'Editing Component...') {
					this.loadingMessage = '';
				}
			};

			document.addEventListener('ts-compact-view', this.compactViewHandler);
			document.addEventListener('ts-compact-day', this.compactDayHandler);
			document.addEventListener('ts-component-modal-closed', this.componentModalClosedHandler);
		},
		beforeUnmount() {
			// these instances are re-created constantly by the keyed v-for, so the
			// listeners must come off or they pile up on a destroyed instance
			document.removeEventListener('ts-compact-view', this.compactViewHandler);
			document.removeEventListener('ts-compact-day', this.compactDayHandler);
			document.removeEventListener('ts-component-modal-closed', this.componentModalClosedHandler);
		},
		watch: {
			// the day's "Expand all / Collapse all" chevron reads its components'
			// real state, so tell it every time this one changes (including the
			// initial compact-view default)
			collapsed: {
				handler(value) {
					this.$emit('collapsedChange', this.component.id, value);
				},
				immediate: true,
			},
		},
		computed: {
			// mirrors the builder header's unpriced rule: sellable card with no
			// client-facing price (legs share the main component's price)
			needsPricing() {
				return !this.component.price
					&& !this.component.is_leg
					&& !this.component.hidden
					&& this.component.component_type !== 'info';
			},
			carLocation() {
				let location = '';

				if (!this.component.details) {
					return location;
				}

				if (this.component.details.pickup_location && this.component.details.dropoff_location) {
					location = 'Pickup at ' + this.component.details.pickup_location + ' and dropoff at ' + this.component.details.dropoff_location;
				} else if (this.component.details.pickup_location) {
					location = 'Pickup at ' + this.component.details.pickup_location;
				} else if (this.component.details.dropoff_location) {
					location = 'Dropoff at ' + this.component.details.dropoff_location;
				}

				return location;
			},
			customHasLocation() {
				if (!this.component.details) {
					return false;
				}

				return this.component.details.google_destination ||
					this.component.details.google_place ||
					this.component.details.business_name ||
					this.component.details.address;
			},
			customLocation() {
				if (!this.component.details) {
					return null;
				}

				if (this.component.details.google_destination && this.component.details.google_place) {
					return this.component.details.google_destination + ' - ' + this.component.details.google_place;
				} else if (this.component.details.google_destination) {
					return this.component.details.google_destination;
				} else if (this.component.details.google_place) {
					return this.component.details.google_place;
				}
				return null;
			},
			// TS-383: an InfoBox shows no stock photo on the card -- only pictures the
			// advisor actually added. The "Add Pictures" affordance stays either way,
			// and the moment an image exists this goes true and the column returns.
			// Narrow on purpose: every other component type keeps its fallback.
			showComponentImage() {
				if (this.component.component_type !== 'info') {
					return true;
				}

				return Boolean(this.component.images && this.component.images.length > 0);
			},
			defaultComponentImage() {
				const componentType = this.component.component_type;
				let componentImage  = componentType;

				// transportation, custom and info components have different names for the default image
				if (componentType === 'transportation') {
					if (this.component.component_subtype === 'car') {
						componentImage = this.component.component_subtype;
					} else {
						componentImage = 'transfer';
					}
				} else if (componentType === 'custom' || componentType === 'info') {
					componentImage = 'other';
				}

				return this.baseUri + '/images/fit_builder_components/component_' + componentImage + '.jpg';
			},
			excursionName() {
				if (!this.component.details) {
					return '';
				}

				return this.component.details.excursion_name + ' - ' + this.component.details.port;
			},
			hotelChain() {
				// an imported hotel can have no chain (TS-396): show what exists, never "null - "
				const parts = [this.component.vendor_name];

				if (this.component.details) {
					parts.push(this.component.details.name);
				}

				return parts.filter((part) => part).join(' - ');
			},
			hotelAddress() {
				let hotelAddress = '';

				if (!this.component.details) {
					return hotelAddress;
				}

				if (this.component.details.address1) {
					hotelAddress = this.component.details.address1;

					if (this.component.details.address2) {
						hotelAddress += ' ' + this.component.details.address2;
					}
				}
				if (this.component.details.city) {
					hotelAddress += '<br>' + this.component.details.city;
				}
				if (this.component.details.state) {
					hotelAddress += ', ' + this.component.details.state;
				}
				if (this.component.details.zipcode) {
					hotelAddress += ' ' + this.component.details.zipcode;
				}
				return hotelAddress;
			},
			isEmbarkationCruise() {
				let isEmbarkation = false;

				if (this.component.component_type === 'cruise') {
					if (
						this.component.details
						&& this.component.details.city
						&& this.component.details.city.toLowerCase().includes('(embark')
					) {
						isEmbarkation = true;
					} else if (
						this.component.component_subtype === 'standard'
						&& this.component.first
					) {
						isEmbarkation = true;
					}
				}

				return isEmbarkation;
			},
			isFirstComponent() {
				// check if this component is first in the array of components for the current day
				if (!this.itinerary) {
					return false;
				}

				const itineraryDay = this.itinerary[this.component.day_number];

				if (!itineraryDay) {
					return false;
				}

				const dayComponents = itineraryDay.components;

				if (!dayComponents || dayComponents.length === 0) {
					return false;
				}

				const index = dayComponents.findIndex(component => component.id === this.component.id);

				return index === 0;
			},
			isLastComponent() {
				// check if this component is last in the array of components for the current day
				if (!this.itinerary) {
					return false;
				}

				const itineraryDay = this.itinerary[this.component.day_number];

				if (!itineraryDay) {
					return false;
				}

				const dayComponents = itineraryDay.components;

				if (!dayComponents || dayComponents.length === 0) {
					return false;
				}

				const index = dayComponents.findIndex(component => component.id === this.component.id);

				return index === dayComponents.length - 1;
			},
			mainImageId() {
				const mainImage = this.getMainComponentImage();
				let mainImageId = null;

				if (mainImage) {
					mainImageId = mainImage.id;
				}

				return mainImageId;
			},
			mainImagePath() {
				const mainImage = this.getMainComponentImage();
				let imagePath   = null;

				if (mainImage) {
					imagePath = mainImage.image_path + encodeURIComponent(mainImage.filename);
				}

				return imagePath;
			},
			mainImageType() {
				const mainImage = this.getMainComponentImage();
				let imageType   = 'image';

				if (mainImage && mainImage.video) {
					imageType = 'video';
				}

				return imageType;
			}
		},
		methods: {
			addComponentMediaEvent(event) {
				if (event.type === 'selected-media') {
					this.addNewImagesToComponent(event.media);
				}

				if (event.type === 'selected-location-images') {
					const images = {
						location_images: event.location_images,
						hotel_images   : event.hotel_images,
					};

					this.addLocationImagesToComponent(images);
				}

				if (event.type === 'close-modal') {
					this.closeAddComponentMediaModal();
				}
			},
			async addLocationImagesToComponent(images) {
				let locationImages = [];
				let hotelImages    = [];

				if (images.location_images) {
					locationImages = images.location_images;
				}

				if (images.hotel_images) {
					hotelImages = images.hotel_images;
				}

				const postData = {
					location_images: locationImages,
					hotel_images   : hotelImages,
				};

				try {
					this.loadingMessage = 'Adding images to component...';

					const url      = '/TripSummaries/addPicturesToComponent/' + this.component.fit_builder_component_id;
					const response = await Adzuki.Ajax.post(url, postData);

					if (response.error) {
						return Adzuki.StatusMessage('error', `Error adding images to component: ${response.error}`);
					}

					const uploadedPictures = response.uploaded_pictures;
					const eventData        = {
						componentId: this.component.fit_builder_component_id,
						images     : uploadedPictures,
					};

					this.$emit('addImagesToComponent', eventData);
				} catch (error) {
					return Adzuki.StatusMessage('error', `Error adding images to component: ${error.message}`);
				} finally {
					this.loadingMessage = '';
				}
			},
			toggleCollapsed(event) {
				// The chevron keeps focus after the click, and focus alone keeps its tooltip
				// open. Hide it and blur. The wording itself comes from the bound
				// data-bs-original-title, which is the attribute Bootstrap 5 reads, so the
				// next hover always shows the current Expand/Collapse text. (Not dispose():
				// it copies the old text back into title, which the next hover re-reads.)
				if (event && event.currentTarget) {
					$(event.currentTarget).tooltip('hide');
					event.currentTarget.blur();
				}

				this.collapsed = !this.collapsed;
			},
			addNewImage() {
				this.hideTooltips();

				this.showAddComponentMediaModal = true;
			},
			async addNewImageOld() {
				const modal   = new bootstrap.Modal('#modalWindow');
				const modalEl = $('#modalWindow');

				modalEl.addClass('wide');
				modalEl.find('.modal-title').text('Add new pictures');
				modalEl.find('.modal-body').text('Loading images...');

				modal.show();
				modalEl.on('hidden.bs.modal', function _restoreModal() {
					modalEl.removeClass('wide').off('hidden.bs.modal');
					modalEl.find('.modal-body').html();
				});

				try {
					const html = await Adzuki.Ajax.get('/TripSummaryPictureLibrary/addImagesToComponent');

					modalEl.find('.modal-body').html(html);

					this.initUploadImagesForm(modal);
					this.initAddImagesFromLibraryForm(modal);
				} catch (error) {
					return Adzuki.StatusMessage('error', `Error adding new image: ${error.message}`);
				}
			},
			async addNewImagesToComponent(mediaIds) {
				// process images added from the media hub
				const postData = {
					media_ids: mediaIds,
				};

				try {
					this.loadingMessage = 'Adding images to component...';

					const url      = '/TripSummaries/addMediaToComponent/' + this.component.fit_builder_component_id;
					const response = await Adzuki.Ajax.post(url, postData);

					if (response.error) {
						return Adzuki.StatusMessage('error', `Error adding media to component: ${response.error}`);
					}

					const uploadedPictures = response.uploaded_pictures;
					const eventData        = {
						componentId: this.component.fit_builder_component_id,
						images     : uploadedPictures,
					};

					this.$emit('addImagesToComponent', eventData);
				} catch (error) {
					return Adzuki.StatusMessage('error', `Error adding media to component: ${error.message}`);
				} finally {
					this.loadingMessage = '';
				}
			},
			attachFiles() {
				this.hideDropdown();

				this.showAttachmentModal = true;
			},
			async attachFilesOld() {
				// this is no longer used, replaced with maxx media
				this.hideDropdown();

				const modal   = new bootstrap.Modal('#modalWindow');
				const modalEl = $('#modalWindow');

				modalEl.addClass('wide');
				modalEl.find('.modal-title').text('Add attachments');
				modalEl.find('.modal-body').text('Loading files...');

				modal.show();
				modalEl.on('hidden.bs.modal', function _restoreModal() {
					modalEl.removeClass('wide').off('hidden.bs.modal');
					modalEl.find('.modal-body').html();
				});

				// register the attach files form
				const form = {
					name: 'AttachFilesToComponent',
				};

				form.load = function load() {
					_initComponentFileDragDropUpload();
				};

				form.success = (response) => {
					if (Adzuki.Form.hasError(response)) {
						return false;
					}

					modal.hide();

					Adzuki.StatusMessage('success', 'The files have been attached to the component');
				};

				// register the form, which will trigger the success callback above on submission
				Adzuki.Form.registerForm(form);

				try {
					const html = await Adzuki.Ajax.get('/TripSummaries/attachFilesToComponent/' + this.component.fit_builder_component_id);

					modalEl.find('.modal-body').html(html);

					Adzuki.Form.initAjaxForm('AttachFilesToComponent');
				} catch (error) {
					return Adzuki.StatusMessage('error', `Error attaching files: ${error.message}`);
				}
			},
			closeAddComponentMediaModal() {
				this.showAddComponentMediaModal = false;
			},
			closeAttachmentModal() {
				this.showAttachmentModal = false;
			},
			closePlayVideoModal() {
				this.showPlayVideoModal = false;
				this.currentVideo       = null;
				this.playVideoHtml      = '';
			},
			closeReplaceDayImageModal() {
				this.showReplaceDayImageModal = false;
			},
			// ADZ-8483: the rules live in ts_schedule_day.js (Adzuki.TripSummaryMoveRules),
			// shared with the drag grip. Move is open to anything that may be
			// reordered; the Move to <day> entries only to what may change day.
			componentCanBeMoved(component) {
				if (!Adzuki.TripSummaryMoveRules.canReorder(component)) {
					return false;
				}

				if (this.componentCanChangeDay(component) || !this.itinerary) {
					return true;
				}

				// held to its day: Move offers only Up / Down, which need a neighbour
				const day = this.itinerary[component.day_number];

				return Boolean(day && day.components && day.components.length > 1);
			},
			componentCanChangeDay(component) {
				return Adzuki.TripSummaryMoveRules.canChangeDay(component, this.sectionMode, this.oneWayCounts);
			},
			deleteComponent(component) {
				this.hideDropdown();

				Adzuki.CustomConfirm.destructive({
					title           : 'Delete “' + Adzuki.CustomConfirm.escape(this.component.component_title || this.component.title) + '”?',
					body            : 'The component will be removed from this trip summary. This can\'t be undone.',
					confirm_btn_text: 'Delete component',
					confirm         : () => {
						this.loadingMessage = 'Deleting Component...';
						this.$emit('delete', component);
					},
				});
			},
			deleteImage(imageId) {
				this.hideTooltips();

				Adzuki.CustomConfirm.destructive({
					title           : 'Delete this picture?',
					body            : 'It will be removed from “' + Adzuki.CustomConfirm.escape(this.component.component_title || this.component.title) + '”. This can\'t be undone.',
					confirm_btn_text: 'Delete picture',
					confirm         : async () => {
						try {
							const url = this.baseUri + '/TripSummaries/deleteComponentImage/' + this.component.fit_builder_component_id + '/' + imageId;

							this.loadingMessage = 'Deleting Media...';

							const response = await Adzuki.Ajax.get(url);

							if (response.error) {
								return Adzuki.StatusMessage('error', `Error deleting media: ${response.error}`);
							}

							this.$emit('deleteComponentImage', imageId);
						} catch (error) {
							return Adzuki.StatusMessage('error', `Error deleting media: ${error.message}`);
						} finally {
							this.loadingMessage = '';
						}
					},
				});
			},
			// tab is optional -- the "No pricing set" chip passes 'pricing' so the
			// editor opens on that tab (TS-364); the builder turns it into a #fragment
			editComponent(component, tab) {
				this.hideDropdown();
				this.loadingMessage = 'Editing Component...';
				this.$emit('edit', component, tab);
			},
			getMainComponentImage() {
				let mainImage    = null;
				const images     = this.component.images || [];

				if (this.component.main_component_image_id) {
					for (const image of images) {
						if (image.id === this.component.main_component_image_id) {
							mainImage = image;
							break;
						}
					}
				}

				// no main component image set for the day, use the flag set in the component details screen
				if (!mainImage) {
					for (const image of images) {
						if (image.main_image) {
							mainImage = image;
							break;
						}
					}
				}

				// no image for the day and no flag, just pick the first image
				if (!mainImage) {
					mainImage = images[0];
				}

				return mainImage;
			},
			getItineraryDayLabel(dayNumber) {
				const day = this.itinerary ? this.itinerary[dayNumber] : null;
				const noun = this.sectionMode ? 'Section' : 'Day';

				return (day && day.label) || `${noun} ${dayNumber}`;
			},
			hideDropdown() {
				const dropdownEl = this.$refs.componentActions;

				// getInstance() returns null when Vue has re-created this element,
				// which would throw and abort the caller mid-way
				if (!dropdownEl || !window.bootstrap) {
					return;
				}

				bootstrap.Dropdown.getOrCreateInstance(dropdownEl).hide();
			},
			async hideUnhideComponent(component, hide) {
				this.hideDropdown();

				if (hide) {
					this.loadingMessage = 'Hiding Component...';
				} else {
					this.loadingMessage = 'Unhiding Component...';
				}

				try {
					let url = this.baseUri + '/TripSummaries/hideUnhideComponent/' + component.fit_builder_id + '/' + component.fit_builder_component_id;

					if (hide) {
						url += '/1';
					} else {
						url += '/0';
					}

					const response = await Adzuki.Ajax.get(url);

					// TS-437: a trip the agent cannot edit is refused; leave the card as it was
					if (response.error) {
						return Adzuki.StatusMessage('error', `Error hiding/unhiding component: ${response.error}`);
					}

					if (response.success) {
						this.$emit('hideUnhideComponent', component, hide);
					}
				} catch (error) {
					return Adzuki.StatusMessage('error', `Error hiding/unhiding component: ${error.message}`);
				} finally {
					this.loadingMessage = '';
				}
			},
			async hideUnhideComponentVendor(component, hide) {
				this.hideDropdown();

				if (hide) {
					this.loadingMessage = 'Hiding Vendor...';
				} else {
					this.loadingMessage = 'Unhiding Vendor...';
				}

				try {
					let url = this.baseUri + '/TripSummaries/hideUnhideComponentVendor/' + component.fit_builder_id + '/' + component.fit_builder_component_id;

					if (hide) {
						url += '/1';
					} else {
						url += '/0';
					}

					const response = await Adzuki.Ajax.get(url);

					// TS-437: a trip the agent cannot edit is refused; leave the card as it was
					if (response.error) {
						return Adzuki.StatusMessage('error', `Error hiding/unhiding component vendor: ${response.error}`);
					}

					if (response.success) {
						this.$emit('hideUnhideComponentVendor', component, hide);
					}
				} catch (error) {
					return Adzuki.StatusMessage('error', `Error hiding/unhiding component vendor: ${error.message}`);
				} finally {
					this.loadingMessage = '';
				}
			},
			hideTooltips() {
				$('[data-toggle="tooltip"]').tooltip('hide');
			},
			async initAddImagesFromLibraryForm(modal) {
				const modalEl = $('#modalWindow');

				try {
					const html = await Adzuki.Ajax.get('/TripSummaryPictureLibrary/addImagesToComponentFromLibrary');

					modalEl.find('.modal-body #component-picture-library').html(html);

					// initialize the image library search form
					Adzuki.Form.initAjaxForm('ImageLibrarySearch');

					// handle selecting images from the image library
					modalEl.find('.ts-add-selected-pictures').first().off().on('click', (e) => {
						e.preventDefault();

						const selectedImages = modalEl.find('.item-checkboxes:checked');
						const imageIds       = [];

						if (selectedImages.length === 0) {
							return Adzuki.StatusMessage('error', 'No images selected');
						}

						selectedImages.each((i, img) => {
							const id = parseInt($(img).val());

							imageIds.push(id);
						});

						this.uploadImagesFromLibrary(imageIds);

						modal.hide();
					});
				} catch (error) {
					return Adzuki.StatusMessage('error', `Error initializing add images from library form: ${error.message}`);
				}
			},
			async initCruiseDayUploadImageForm(modal) {
				const modalEl = $('#modalWindow');

				// register the form
				const form = {
					name: 'CruiseDayImage',
				};

				form.load = function load() {
					_initCruiseDayDragDropUpload();
					_initCruiseDayLocationSearch();
				};

				form.success = (response) => {
					if (Adzuki.Form.hasError(response)) {
						return false;
					}

					modal.hide();

					const image = response.data.image;

					this.updateComponentCruiseDayImage(image);
				};

				Adzuki.Form.registerForm(form);

				try {
					const html = await Adzuki.Ajax.get('/TripSummaryPictureLibrary/addCruiseDayImage');

					modalEl.find('.modal-body #load-new-picture').html(html);

					// initialize the form, which will trigger the success callback above on submission
					Adzuki.Form.initAjaxForm('CruiseDayImage');
				} catch (e) {
					Adzuki.StatusMessage('error', `Error loading image library: ${e.message}`);
				}
			},
			async initCruiseDayAddImageFromLibraryForm(modal) {
				const modalEl = $('#modalWindow');

				try {
					const html = await Adzuki.Ajax.get('/TripSummaryPictureLibrary/addCruiseDayImageFromLibrary');

					modalEl.find('.modal-body #library-picture').html(html);

					// initialize the image library search form
					Adzuki.Form.initAjaxForm('ImageLibrarySearch');

					// handle selecting images from the image library
					modalEl.find('.ts-add-selected-pictures').first().off().on('click', (e) => {
						e.preventDefault();

						const selectedImages = modalEl.find('.item-checkboxes:checked');
						const imageIds       = [];

						if (selectedImages.length === 0) {
							Adzuki.StatusMessage('error', 'No images selected');
							return;
						}

						if (selectedImages.length > 1) {
							Adzuki.StatusMessage('error', 'Only one image can be added to a cruise day');
							return;
						}

						selectedImages.each((i, img) => {
							const id = parseInt($(img).val());

							imageIds.push(id);
						});

						this.setCruiseDayImageFromLibrary(imageIds[0]);

						modal.hide();
					});
				} catch (error) {
					return Adzuki.StatusMessage('error', `Error initializing add images from library form: ${error.message}`);
				}
			},
			async initUploadImagesForm(modal) {
				const modalEl = $('#modalWindow');

				// register the upload form
				const form = {
					name: 'UploadPicturesToComponent',
				};

				form.load = function load() {
					_initComponentImageDragDropUpload();
					_initComponentImageLocationSearch();
					_initComponentHotelImageSelection();
				};

				form.success = (response) => {
					if (Adzuki.Form.hasError(response)) {
						return false;
					}

					const uploadedPictures = response.data.uploaded_pictures;
					const eventData = {
						componentId: this.component.fit_builder_component_id,
						images     : uploadedPictures,
					};

					this.$emit('addImagesToComponent', eventData);

					modal.hide();
				};

				// initialize the form, which will trigger the success callback above on submission
				Adzuki.Form.registerForm(form);

				try {
					const html = await Adzuki.Ajax.get('/TripSummaryPictureLibrary/uploadImagesToComponent/' + this.component.fit_builder_component_id);

					modalEl.find('.modal-body #load-new-pictures').html(html);

					Adzuki.Form.initAjaxForm('UploadPicturesToComponent');
				} catch (error) {
					return Adzuki.StatusMessage('error', `Error initializing upload images form: ${error.message}`);
				}
			},
			isMainImage(image) {
				const mainImage = this.getMainComponentImage();

				return image.id === mainImage.id;
			},
			async managePromotions() {
				this.hideDropdown();

				const canvasEl = $('#offcanvas');
				const canvas   = new bootstrap.Offcanvas(canvasEl[0]);

				$('.offcanvas-title').text('Manage Promotions');
				$('.offcanvas-body').html('Loading promotions...');

				canvas.show();

				try {
					const url = '/TripSummaries/manageComponentPromotions/' + this.component.fit_builder_component_id;

					const response = await Adzuki.Ajax.get(url);

					if (response.error) {
						return Adzuki.StatusMessage('error', `Error loading promotions: ${response.error}`);
					}

					$('.offcanvas-body').html(response);

					Adzuki.Form.initAjaxForm('ManageComponentPromotions');
				} catch (error) {
					return Adzuki.StatusMessage('error', `Error loading promotions: ${error.message}`);
				}

				canvasEl.on('hidden.bs.offcanvas', function _restoreCanvas() {
					canvasEl.off('hidden.bs.offcanvas');
					$('.offcanvas-body').html('');
				});
			},
			moveComponent(component, target) {
				this.hideDropdown();

				// a disabled Move to entry is only styled; refuse it here too
				if (target !== 'up' && target !== 'down' && !this.componentCanChangeDay(component)) {
					return;
				}

				const eventData = {
					id       : component.id,
					dayNumber: parseInt(component.day_number),
					target   : target,
				};

				this.$emit('moveComponent', eventData);
			},
			playCruiseDayVideo(videoUrl) {
				this.showPlayVideoModal = true;
				this.currentVideo       = videoUrl;
				const videoSrc          = this.baseUri + '/' + videoUrl;

				this.playVideoHtml = '<div class="text-center"><video controls width="840" height="480" playsinline autoplay muted><source src="' + videoSrc + '" type="video/mp4">Your browser does not support the video tag.</video></div>';
			},
			playVideo(imageId) {
				if (!this.component.images) {
					return;
				}

				const image = this.component.images.find(i => i.id === imageId);

				if (!image) {
					return;
				}

				this.showPlayVideoModal = true;
				this.currentVideo       = image;
				let videoSrc            = this.baseUri + '/' + image.image_path + encodeURIComponent(image.video);

				this.playVideoHtml = '<div class="text-center"><video controls width="840" height="480" playsinline autoplay muted><source src="' + videoSrc + '" type="video/mp4">Your browser does not support the video tag.</video></div>';
			},
			replaceCruiseDayEvent(event) {
				if (event.type === 'selected-image') {
					this.updateComponentCruiseDayImage(event);
				}

				if (event.type === 'close-modal') {
					this.closeReplaceDayImageModal();
				}
			},
			replaceDayImage() {
				this.hideTooltips();

				this.selectedCruiseDay = this.component.details;

				this.showReplaceDayImageModal = true;
			},
			async replaceDayImageOld() {
				const modal   = new bootstrap.Modal('#modalWindow');
				const modalEl = $('#modalWindow');

				modalEl.addClass('wide');
				modalEl.find('.modal-title').text('Replace cruise day image');
				modalEl.find('.modal-body').text('Loading details...');

				modal.show();
				modalEl.on('hidden.bs.modal', function _restoreModal() {
					modalEl.removeClass('wide').off('hidden.bs.modal');
					modalEl.find('.modal-body').html('');
				});

				try {
					const html = await Adzuki.Ajax.get('/TripSummaryPictureLibrary/cruiseDayImage');

					modalEl.find('.modal-body').html(html);

					this.initCruiseDayUploadImageForm(modal);
					this.initCruiseDayAddImageFromLibraryForm(modal);
				} catch (e) {
					Adzuki.StatusMessage('error', `Error loading image library: ${e.message}`);
				}
			},
			async saveToLibrary(component) {
				this.hideDropdown();
				this.loadingMessage = 'Saving to Library...';

				try {
					let url = this.baseUri + '/TripSummaries/saveToLibrary/' + component.fit_builder_id + '/' + component.fit_builder_component_id;

					const response = await Adzuki.Ajax.get(url);

					if (response.error) {
						return Adzuki.StatusMessage('error', `Error saving to library: ${response.error}`);
					}

					return Adzuki.StatusMessage('success', 'The component has been saved to the library');
				} catch (error) {
					return Adzuki.StatusMessage('error', `Fatal error saving to library: ${error.message}`);
				} finally {
					this.loadingMessage = '';
				}
			},
			async selectAttachment(media) {
				if (!media.media_id) {
					this.showAttachmentModal = false;
					return;
				}

				const mediaIds = [];

				if (Array.isArray(media.media_id)) {
					mediaIds.push(...media.media_id);
				} else {
					mediaIds.push(media.media_id);
				}

				try {
					const url      = '/TripSummaries/attachMediaFilesToComponent/' + this.component.fit_builder_component_id;
					const response = await Adzuki.Ajax.post(url, {
						media_ids: mediaIds
					});

					if (response.error) {
						return Adzuki.StatusMessage('error', `Error attaching files to component: ${response.error}`);
					}

					if (response.warning) {
						return Adzuki.StatusMessage('warning', response.warning);
					}

					return Adzuki.StatusMessage('success', 'The files have been attached to the component');
				} catch (error) {
					return Adzuki.StatusMessage('error', `Error attaching files to component: ${error.message}`);
				} finally {
					this.showAttachmentModal = false;
				}
			},
			async setCruiseDayImageFromLibrary(imageId) {
				try {
					const url      = '/TripSummaryPictureLibrary/getImagePath/' + imageId;
					const response = await Adzuki.Ajax.get(url);

					if (response.error) {
						Adzuki.StatusMessage('error', `Error setting cruise day image from library: ${response.error}`);
						return;
					}

					const imagePath = response.image_path + response.image;

					this.updateComponentCruiseDayImage(imagePath);
				} catch (error) {
					Adzuki.StatusMessage('error', `Error setting the cruise day image from library: ${error.message}`);
					return;
				}
			},
			async setMainImage(image) {
				this.hideTooltips();

				let detailId = null;
				let isLeg    = '0';
				let isFirst  = '0';

				if (this.component.details && this.component.details.id) {
					detailId = this.component.details.id;
				}

				if (this.component.is_leg) {
					isLeg = '1';
				}

				if (this.component.first) {
					isFirst = '1';
				}

				try {
					const url      = '/TripSummaries/updateComponentDayMainImage/' + this.component.fit_builder_component_id + '/' + image.id;
					const postData = {
						detail_id: detailId,
						is_leg   : isLeg,
						is_first : isFirst,
					};

					const response = await Adzuki.Ajax.post(url, postData);

					if (response.error) {
						return Adzuki.StatusMessage('error', `Error setting main media: ${response.error}`);
					}

					const eventData = {
						id           : this.component.id,
						componentId  : this.component.fit_builder_component_id,
						componentType: this.component.component_type,
						isLeg        : this.component.is_leg,
						isFirst      : this.component.first,
						detailId     : detailId,
						image        : image,
					};

					this.$emit('setComponentMainImage', eventData);
				} catch (error) {
					return Adzuki.StatusMessage('error', `Error setting main media: ${error.message}`);
				}
			},
			async setShoreExcursions() {
				this.hideDropdown();
				const modal   = new bootstrap.Modal('#modalWindow');
				const modalEl = $('#modalWindow');

				// ts-shorex-modal lets the selection bar stick while the modal scrolls
				// (MEDIA-343)
				modalEl.addClass('wide ts-shorex-modal');
				modalEl.find('.modal-title').text('Shore excursions');
				modalEl.find('.modal-body').text('Loading excursions...');

				modal.show();
				modalEl.on('hidden.bs.modal', function _restoreModal() {
					modalEl.removeClass('wide ts-shorex-modal').off('hidden.bs.modal');
					modalEl.find('.modal-body').html();
				});

				try {
					const componentId  = this.component.fit_builder_component_id;
					const fitBuilderId = this.component.fit_builder_id;
					const baseUri      = this.baseUri;
					const url          = '/TripSummaries/excursions/' + componentId;
					const response     = await Adzuki.Ajax.get(url);

					if (!response) {
						return Adzuki.StatusMessage('error', `Error retrieving excursions: ${response.error}`);
					}

					modalEl.find('.modal-body').html(response);

					// MEDIA-343: each card is the label of its round pick, so a click anywhere
					// on it (or Space on the pick) toggles it; the selection bar counts the
					// picks and its Add selected does what Continue did
					const $picks = modalEl.find('.ts-shorex-pick');
					const $bulk  = modalEl.find('.ts-shorex-bulk');

					const updateSelection = () => {
						const count = $picks.filter(':checked').length;

						$picks.each(function _markCard() {
							$(this).closest('.ts-shorex-card').toggleClass('is-selected', this.checked);
						});

						modalEl.find('.ts-shorex-count').text(count);
						$bulk.toggleClass('d-none', count === 0);
					};

					$picks.off().on('change', updateSelection);

					// a chevron after a title or teaser the card cuts short opens the card's
					// full text; a button, so it never toggles the card's pick
					modalEl.find('.ts-shorex-card').each(function _addMoreToggle() {
						const $card   = $(this);
						const clamped = $card.find('.ts-shorex-title, .ts-shorex-teaser').filter(function _isClamped() {
							return this.scrollHeight > this.clientHeight + 1;
						});

						if (!clamped.length) {
							return;
						}

						$('<button type="button" class="ts-shorex-more" aria-expanded="false" aria-label="Show the full description" data-toggle="tooltip" data-bs-original-title="Show more"><i class="fas fa-chevron-down" aria-hidden="true"></i></button>')
							.insertAfter($card.find('.ts-shorex-teaser'))
							.on('click', function _toggleMore(event) {
								event.preventDefault();
								event.stopPropagation();

								const expanded = !$card.hasClass('is-expanded');

								$card.toggleClass('is-expanded', expanded);
								$(this)
									.attr('aria-expanded', expanded ? 'true' : 'false')
									.attr('aria-label', expanded ? 'Show less of the description' : 'Show the full description')
									.attr('data-bs-original-title', expanded ? 'Show less' : 'Show more')
									.find('i')
									.toggleClass('fa-chevron-down', !expanded)
									.toggleClass('fa-chevron-up', expanded);
							});
					});

					modalEl.find('.ts-shorex-clear').off().on('click', function _clearExcursions() {
						$picks.prop('checked', false);
						updateSelection();
					});

					modalEl.find('.fb-excursion-process').first().off().on('click', function _processExcursions(e) {
						e.preventDefault();

						const excursionList = $picks.filter(':checked').map(function _code() {
							return $(this).val();
						}).get();
						const itineraryId = $('#fb-excursion-itinerary-id').val();

						if (excursionList.length > 0) {
							// the next page asks SEG for the picks' dates, which takes a moment
							$(this).prop('disabled', true).html('<i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Adding&hellip;');

							let url = '/TripSummaries/seg/' + fitBuilderId + '/' + itineraryId + '/' + excursionList.join(',');
							document.location = baseUri + url;
						} else {
							Adzuki.StatusMessage('error', 'Please select at least one excursion');
						}
					});
				} catch (error) {
					return Adzuki.StatusMessage('error', `Error retrieving excursions: ${error.message}`);
				}
			},
			async updateComponentCruiseDayImage(cruiseDay) {
				const url         = this.baseUri + '/TripSummaries/updateComponentCruiseDayImage/' + this.component.fit_builder_component_id + '/' + this.component.details.id;
				const requestData = { image_path: cruiseDay.image };

				if (cruiseDay.video) {
					requestData.video_path = cruiseDay.video;
				}

				this.loadingMessage = 'Updating Cruise Day Media...';

				try {
					const response = await Adzuki.Ajax.post(url, requestData);

					if (response.error) {
						Adzuki.StatusMessage('error', `Error updating cruise day image: ${response.error}`);
						return;
					}

					const eventData = {
						id      : this.component.id,
						detailId: parseInt(this.component.details.id),
						image   : cruiseDay.image,
					};

					if (cruiseDay.video) {
						eventData.video = cruiseDay.video;
					}

					this.loadingMessage = '';

					this.$emit('updateComponentCruiseDayImage', eventData);
				} catch (error) {
					return Adzuki.StatusMessage('error', `Error updating cruise day image: ${error.message}`);
				} finally {
					this.loadingMessage = '';
				}
			},
			// TS-309 inline rename -------------------------------------------
			//
			// Cruise is excluded: its rows are generated per port from the sailing,
			// so a rename here would fight the itinerary. The server refuses it too.
			canEditTitle() {
				return this.component.component_type !== 'cruise';
			},
			startEditingTitle() {
				if (!this.canEditTitle()) {
					return;
				}

				// The pencil is about to be destroyed by the v-if, and Bootstrap leaves
				// its tooltip attached to the body -- with no anchor left to position
				// against it renders in the top-left corner of the page.
				this.hideTooltips();

				// edit the COMPONENT's title, not the suffixed sort-row title shown
				this.titleDraft   = this.component.component_title || this.component.title;
				this.editingTitle = true;

				this.$nextTick(() => {
					if (this.$refs.titleInput) {
						this.$refs.titleInput.focus();
						this.$refs.titleInput.select();
					}
				});
			},
			cancelEditingTitle() {
				// same again for the Save and Cancel anchors -- saveComponentTitle()
				// ends here, which is the path QA saw the orphan on
				this.hideTooltips();

				this.editingTitle = false;
				this.titleDraft   = '';
			},
			async saveComponentTitle() {
				const newTitle = (this.titleDraft || '').trim();
				const oldTitle = this.component.component_title || '';

				if (!newTitle || newTitle === oldTitle) {
					return this.cancelEditingTitle();
				}

				const url = this.baseUri + '/TripSummaries/updateComponentTitle/' + this.component.fit_builder_component_id;

				this.loadingMessage = 'Saving title...';

				try {
					const response = await Adzuki.Ajax.post(url, { title: newTitle });

					if (response.error) {
						return Adzuki.StatusMessage('error', response.error);
					}

					// mirror the server: swap the old component title for the new one at
					// the front of the displayed sort-row title, keeping its suffix
					const retitle = (card) => {
						const shown = card.title || '';

						if (shown.indexOf(oldTitle) === 0) {
							card.title = newTitle + shown.substring(oldTitle.length);
						} else {
							card.title = newTitle;
						}

						card.component_title = newTitle;
					};

					retitle(this.component);

					// TS-449: a connecting flight has a card per leg, on other days too;
					// the server renamed every one of them
					for (const day of Object.values(this.itinerary || {})) {
						for (const card of (day.components || [])) {
							if (card !== this.component && card.fit_builder_component_id === this.component.fit_builder_component_id) {
								retitle(card);
							}
						}
					}

					this.$emit('updateComponentTitle', {
						fit_builder_component_id: this.component.fit_builder_component_id,
						title                   : newTitle,
					});

					this.cancelEditingTitle();
				} catch (error) {
					Adzuki.StatusMessage('error', `Error saving title: ${error.message}`);
				} finally {
					this.loadingMessage = '';
				}
			},
			updateComponentDescription(eventData) {
				this.$emit('updateComponentDescription', eventData);
			},
			updateComponentTravelers(eventData) {
				this.$emit('updateComponentTravelers', eventData);
			},
			async uploadImagesFromLibrary(imageIds) {
				const url = '/TripSummaryPictureLibrary/uploadImagesToComponentFromLibrary/' + this.component.fit_builder_component_id;

				this.loadingMessage = 'Uploading images from library...';

				try {
					const response = await Adzuki.Ajax.post(url, {
						image_ids: imageIds,
					});

					if (response.error) {
						return Adzuki.StatusMessage('error', `Error uploading images from library: ${response.error}`);
					}

					const uploadedPictures = response;
					const eventData        = {
						componentId: this.component.fit_builder_component_id,
						images     : uploadedPictures,
					};

					this.$emit('addImagesToComponent', eventData);
				} catch (error) {
					return Adzuki.StatusMessage('error', `Error uploading images from library: ${error.message}`);
				} finally {
					this.loadingMessage = '';
				}
			},
		},
	};

	function _initComponentFileDragDropUpload() {
		Adzuki.DragDropUpload.start($('.component-file-upload .file-upload-options'));

		$(document).on('click', '.component-file-upload .file-upload-options .attachment-remove', function click(e) {
			const $self = $(this);

			e.stopPropagation();
			e.preventDefault();

			Adzuki.DragDropUpload.removeAttachment($self);
		});
	}

	function _initComponentHotelImageSelection() {
		let selectedImages = [];

		$('.hotel-property-images').on('click', '.hotel-property-image', function _selectPictures() {
			let image = null;
			const src = $(this).data('src');

			if ($(this).hasClass('google-image-active')) {
				$(this).removeClass('google-image-active');

				image = selectedImages.indexOf(src);

				if (image > -1) {
					selectedImages.splice(image, 1);
				}
			} else {
				selectedImages.push(src);

				$(this).addClass('google-image-active');
			}

			$('#uploadpicturestocomponent-selected_hotel_images').val(JSON.stringify(selectedImages));
		});
	}

	function _initComponentImageDragDropUpload() {
		Adzuki.DragDropUpload.start($('.component-image-upload .file-upload-options'));

		$(document).on('click', '.component-image-upload .file-upload-options .attachment-remove', function click(e) {
			const $self = $(this);

			e.stopPropagation();
			e.preventDefault();

			Adzuki.DragDropUpload.removeAttachment($self);
		});

		$(document).on('click', '.component-image-upload .file-upload-options .attachment-view', function click(e) {
			const $self  = $(this);
			let in_modal = false;

			e.stopPropagation();
			e.preventDefault();

			if ($self.parents('div.modal-body').length) {
				in_modal = true;
			}

			Adzuki.DragDropUpload.viewAttachment($self, in_modal);
		});
	}

	function _initComponentImageLocationSearch() {
		let selectedImages = [];

		Adzuki.Typeahead(
			'#uploadpicturestocomponent-location_term',
			'/Searches/googleAutocomplete/',
			async function select(item) {
				try {
					const response = await Adzuki.Ajax.get('/Searches/googlePlace/' + item.id);

					$('.pictures').html(response);
					$('#uploadpicturestocomponent-location_term').prop('disabled', true);
					$('.reset-component-image-location-search').removeClass('d-none');
				} catch (e) {
					Adzuki.StatusMessage('error', `Error searching for location: ${e.message}`);
				}
			},
			function template(item) {
				return item.name;
			}
		);

		$('.location-pictures').on('click', '.google-image', function _selectPictures() {
			let image = null;
			const src = $(this).data('src');

			if ($(this).hasClass('google-image-active')) {
				$(this).removeClass('google-image-active');

				image = selectedImages.indexOf(src);

				if (image > -1) {
					selectedImages.splice(image, 1);
				}
			} else {
				selectedImages.push(src);

				$(this).addClass('google-image-active');
			}

			$('#uploadpicturestocomponent-selected_images').val(JSON.stringify(selectedImages));
		});

		$('.reset-component-image-location-search').on('click', function _clearLocation(e) {
			e.preventDefault();

			$('#uploadpicturestocomponent-location_term').val('');
			$('#uploadpicturestocomponent-location_term').prop('disabled', false);
			$('.reset-component-image-location-search').addClass('d-none');
			$('#uploadpicturestocomponent-selected_images').val('');
			$('.location-pictures').html('');
		});
	}

	function _initCruiseDayDragDropUpload() {
		Adzuki.DragDropUpload.start($('.cruise-day-image-upload .file-upload-options'));

		$(document).on('click', '.cruise-day-image-upload .file-upload-options .attachment-remove', function click(e) {
			const $self = $(this);

			e.stopPropagation();
			e.preventDefault();

			Adzuki.DragDropUpload.removeAttachment($self);
		});

		$(document).on('click', '.cruise-day-image-upload .file-upload-options .attachment-view', function click(e) {
			const $self  = $(this);
			let in_modal = false;

			e.stopPropagation();
			e.preventDefault();

			if ($self.parents('div.modal-body').length) {
				in_modal = true;
			}

			Adzuki.DragDropUpload.viewAttachment($self, in_modal);
		});
	}

	function _initCruiseDayLocationSearch() {
		Adzuki.Typeahead(
			'#cruisedayimage-location_term',
			'/Searches/googleAutocomplete/',
			async function select(item) {
				try {
					const response = await Adzuki.Ajax.get('/Searches/googlePlace/' + item.id);

					$('.pictures').html(response);
					$('#cruisedayimage-location_term').prop('disabled', true);
					$('.reset-day-location-search').removeClass('d-none');
				} catch (e) {
					Adzuki.StatusMessage('error', `Error searching for location: ${e.message}`);
				}
			},
			function template(item) {
				return item.name;
			}
		);

		$('.pictures').on('click', '.google-image', function _selectPicture() {
			const src  = $(this).data('src');
			const $obj = $(this);
			
			$('.google-image').removeClass('google-image-active');

			$obj.addClass('google-image-active');

			$('#cruisedayimage-image_url').val(src);
		});

		$('.reset-day-location-search').on('click', function _clearLocation(e) {
			e.preventDefault();

			$('#cruisedayimage-location_term').val('');
			$('#cruisedayimage-location_term').prop('disabled', false);
			$('.reset-day-location-search').addClass('d-none');
			$('.pictures').html('');
		});
	}

	Adzuki.VueComponents.TripSummaryScheduleComponent = component;
}(jQuery, window.Adzuki || {}));
